import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CartoesDeData } from "@/components/equipe/CartoesDeData";
import { BlocoMensalistas } from "@/components/equipe/BlocoMensalistas";
import { GerarAdiantamentosDialog, FechamentoMensalDialog } from "@/components/equipe/DialogosFase2";
import {
  calcTotalASairQuinzena, competenciaDaQuinzena, formatarBRL, formatarDataCurta,
  paraCentavos, paraReais, ehMensalista, apareceComoDiarista, formatarKg, formatarResumoAdicional,
  agruparAdicionaisPorFuncionario, totalAdicionaisCentavos as somarLinhasAdicional,
  totalAdiantamentosMensalistasCentavos, rotuloTipoAdicional, netLancamentosCentavos,
  rotuloSinalLancamento,
  lancamentosNoCaixaDaQuinzena, totalSalariosDoMesCentavos, dataPagamentoDaCompetencia, proximaCompetencia,
  COMPETENCIA_RE,
  type AdicionalResumoPorTipo,
} from "@shared/equipe-fechamento";
import { PageTabs } from "@/components/layout/page-tabs";
import { SaiJuntoNestaData } from "@/components/equipe/SaiJuntoNestaData";
import { ReciboDialog } from "@/components/equipe/ReciboDialog";
import { montarReciboQuinzena, tituloRecibo } from "@shared/equipe-recibo";
import { FinanceiroMes } from "./financeiro-mes";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { calcFormula } from "@shared/equipe-formula";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Lock, CreditCard, AlertTriangle, Users, Share2 } from "lucide-react";
import {
  useQuinzenas,
  useFuncionarios,
  useDiariasByQuinzena,
  useMensalistasDaQuinzena, useDefinirDataPagamento, useOrganizacao,
  useFechamentos,
  useAtividades,
  useTarefasByPeriodo,
  useUpdateTarefa,
  useAdicionaisDaQuinzena,
  type AdicionalAgregado,
} from "@/hooks/use-equipe";
import { FecharQuinzenaDialog } from "@/components/equipe/fechar-quinzena-dialog";
import { PagarQuinzenaDialog } from "@/components/equipe/pagar-quinzena-dialog";
import { TarefaDiariaSheet, type DiariaParaEditar, type TarefaParaEditar } from "@/components/equipe/TarefaDiariaSheet";
import { FecharQuinzenaSheet } from "@/components/equipe/FecharQuinzenaSheet";
import { NovaQuinzenaSheet } from "@/components/equipe/NovaQuinzenaSheet";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { format, eachDayOfInterval } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { Diaria, Funcionario, Quinzena } from "@shared/schema";

import { PageHeader } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { avatarCor } from "@/lib/avatar-cor";
// ─── Constants ────────────────────────────────────────────────────────────────

// Tom semântico por status da quinzena. Substitui os três pares de classes
// fixas que viviam aqui — a mesma tabela de tons das Vendas e da Irrigação.
const STATUS_TOM: Record<string, Tom> = {
  ABERTA: "atencao",
  FECHADA: "info",
  PAGA: "sucesso",
};

/**
 * Financeiro v3.1 (D-M1) — DUAS abas, DOIS calendários. "Diaristas · quinzena"
 * responde "quem recebe quanto nesta data de pagamento?"; "Mensalistas · mês"
 * responde "quanto cada mensalista recebe no fechamento desta competência?".
 * Separadas pela PERGUNTA, não pelo regime. Estado na URL (`?aba=`).
 */
const ABAS_FINANCEIRO = [
  { id: "quinzena", label: "Diaristas · quinzena" },
  { id: "mes", label: "Mensalistas · mês" },
];

const TIPO_LABELS: Record<string, string> = {
  NORMAL: "Diária",
  MEIA_DIARIA: "Meia Diária",
  HORA_EXTRA: "Hora extra",
  EMPREITA: "Empreita",
  MENSALISTA: "Mensalista",
  DESCONTO: "Desconto",
  ADIANTAMENTO: "Adiantamento",
};

function avatarInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nome.slice(0, 2).toUpperCase();
}



function formatMoney(n: number): string {
  return `R$ ${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

// ─── Mobile sub-component ─────────────────────────────────────────────────────

interface MobileFinanceiroProps {
  quinzenas: Quinzena[];
  quinzenaIdx: number;
  setQuinzenaIdx: (idx: number) => void;
  quinzena: Quinzena | undefined;
  diarias: Diaria[];
  funcionarios: Funcionario[];
  isAdmin: boolean;
  diaStatusMap: Map<string, "FALTOU" | "PENDENTE" | "CONCLUIDA">;
  onLancarDiaria: () => void;
  onFechar: () => void;
  onPagar: () => void;
  onNovaQuinzena: () => void;
  onOpenDayPanel: (funcId: string, funcNome: string, dateStr: string) => void;
  /** Recibo da quinzena da pessoa — texto pronto para mandar. */
  onRecibo: (funcId: string) => void;
  atividades: { id: string; nome: string }[];
  tarefas: { funcionarioId: string; data: string; tipo: string | null; atividadeId: string | null }[];
  // filter
  funcsExcluidos: Set<string>;
  isVisivel: (id: string) => boolean;
  toggleFunc: (id: string) => void;
  marcarTodos: () => void;
  limparTodos: () => void;
  nVisiveis: number;
  filtrando: boolean;
  // Fase 2 — montados no pai (os dados vêm de endpoint próprio, separado da
  // folha). Três pedaços, não um só: §1/§2 exigem Diaristas ANTES de
  // Mensalistas, então o bloco de mensalistas não pode ir junto dos cartões
  // no topo — só cartões + ações sobem para o topbar.
  cartoesEAcoes: React.ReactNode;
  mensalistasBlock: React.ReactNode;
  adicionaisPlaceholder: React.ReactNode;
  totalASair: string;
  totalAdiantamentos: string;
  // Fase 3 — agregado por pessoa, uma linha por tipo (§2.2), para o card e o rodapé.
  adicionalPorFuncionario: Map<string, AdicionalResumoPorTipo[]>;
  totalAdicionaisReais: string;
}

function MobileFinanceiro({
  quinzenas,
  quinzenaIdx,
  setQuinzenaIdx,
  quinzena,
  diarias,
  funcionarios,
  isAdmin,
  diaStatusMap,
  onLancarDiaria,
  onFechar,
  onPagar,
  onNovaQuinzena,
  onOpenDayPanel,
  onRecibo,
  atividades,
  tarefas,
  funcsExcluidos,
  isVisivel,
  toggleFunc,
  marcarTodos,
  limparTodos,
  nVisiveis,
  filtrando,
  cartoesEAcoes,
  mensalistasBlock,
  adicionaisPlaceholder,
  totalASair,
  totalAdiantamentos,
  adicionalPorFuncionario,
  totalAdicionaisReais,
}: MobileFinanceiroProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const toggleId = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!quinzena) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 px-4">
        <p className="text-muted-foreground text-sm">Nenhuma quinzena encontrada.</p>
        {isAdmin && (
          <button
            onClick={onNovaQuinzena}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            Criar quinzena
          </button>
        )}
      </div>
    );
  }

  // Metrics — respect filter for totals/counts
  const diariasVisiveis = diarias.filter(d => isVisivel(d.funcionarioId));
  const totalGeral = diariasVisiveis.reduce((acc, d) => acc + parseFloat(d.valor), 0);
  const funcIdsComDiaria = new Set(diarias.map(d => d.funcionarioId));
  const numFunc = new Set(diariasVisiveis.map(d => d.funcionarioId)).size;
  const numDiarias = diariasVisiveis.length;

  const atividadeNome = new Map(atividades.map(a => [a.id, a.nome]));

  const ativsDoDia = (funcId: string, data: string, tipo: string): string => {
    const nomes = tarefas
      .filter(t => t.funcionarioId === funcId && t.data === data && (t.tipo ?? "NORMAL") === tipo)
      .map(t => atividadeNome.get(t.atividadeId ?? ""))
      .filter((n): n is string => !!n);
    return Array.from(new Set(nomes)).join(", ");
  };

  // Per-funcionario data sorted by total desc — only visible funcs.
  // §3 (fix pós-#116) — a entrada é o FATO HISTÓRICO (tem diária tipo!=MENSALISTA
  // nesta quinzena — `diarias` já vem filtrado assim, ver "PONTO DE SOMA DE
  // FOLHA" acima), não o regime ATUAL. Filtrar por `!ehMensalista(f)` aqui
  // duplicava o critério da query — só que com um critério DIFERENTE do que
  // soma o TOTAL, então quem trocasse de regime sumia da lista mas o dinheiro
  // continuava no total (o bug do Erivelton). `diarias` já garante que ninguém
  // cujas diárias são só REGISTRO entra aqui — não precisa (e não pode) filtrar
  // de novo por regime.
  const funcData = funcionarios
    .filter(f => apareceComoDiarista(f, funcIdsComDiaria.has(f.id), false) && isVisivel(f.id))
    .map(f => {
      const fd = diarias.filter(d => d.funcionarioId === f.id);
      const total = fd.reduce((acc, d) => acc + parseFloat(d.valor), 0);
      const diasTrabalhados = new Set(fd.filter(d => d.tipo === "NORMAL" && parseFloat(d.valor) > 0).map(d => d.data)).size;
      // Marcador espelho do "era mensalista nesta quinzena" do outro bloco.
      const eraDiarista = ehMensalista(f);
      return { func: f, diarias: fd.sort((a, b) => a.data.localeCompare(b.data)), total, diasTrabalhados, eraDiarista };
    })
    .sort((a, b) => b.total - a.total);

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Topbar — sem título: o cabeçalho é um só, hoisted no componente pai. */}
      <div className="bg-card px-4 pt-4 pb-3 border-b border-border">
        {/* Period nav */}
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => setQuinzenaIdx(Math.min(quinzenaIdx + 1, quinzenas.length - 1))}
            disabled={quinzenaIdx >= quinzenas.length - 1}
            className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center">
            <p className="text-sm font-medium text-foreground">
              {format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM")}
              {" – "}
              {format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM/yyyy")}
            </p>
          </div>
          <button
            onClick={() => setQuinzenaIdx(0)}
            className="text-xs text-sucesso font-medium px-2 py-1 rounded-md hover:bg-sucesso-suave"
          >
            Atual
          </button>
          <button
            onClick={() => setQuinzenaIdx(Math.max(quinzenaIdx - 1, 0))}
            disabled={quinzenaIdx <= 0}
            className="h-8 w-8 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* §1 itens 3-4 — cartões de data + ações (Gerar adiantamentos,
            Fechamento do mês) logo abaixo do período: é a primeira pergunta de
            quem abre esta tela no celular. "Lançar diária" fica na barra fixa
            do rodapé (padrão já estabelecido desta tela, thumb-reachable). */}
        <div className="mt-3">{cartoesEAcoes}</div>
      </div>

      {/* Filter chip */}
      <div className="px-4 pt-2 pb-1 flex items-center gap-2">
        <button
          onClick={() => setFilterSheetOpen(true)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
            filtrando
              ? "bg-sucesso-suave border-sucesso/30 text-sucesso"
              : "bg-card border-border text-muted-foreground"
          }`}
        >
          <Users className="h-3.5 w-3.5" />
          Funcionários{" "}
          <span className={`font-bold ${filtrando ? "text-sucesso" : "text-foreground"}`}>
            {nVisiveis}/{funcionarios.length}
          </span>
        </button>
        {filtrando && (
          <button
            onClick={marcarTodos}
            className="text-xs text-muted-foreground underline underline-offset-2"
          >
            Ver todos
          </button>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3 px-4 pt-4">
        {[
          { label: "Total", value: formatMoney(totalGeral) },
          { label: "Funcionários", value: String(numFunc) },
          { label: "Lançamentos", value: String(numDiarias) },
        ].map(m => (
          <div key={m.label} className="bg-card rounded-xl p-3 border border-border text-center">
            <p className="text-xs text-muted-foreground mb-0.5">{m.label}</p>
            <p className="text-sm font-bold text-foreground truncate">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Closed banner */}
      {quinzena.status === "FECHADA" && (
        <div className="mx-4 mt-3 bg-info-suave border border-info/30 rounded-xl px-3 py-2 text-xs text-info flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Quinzena fechada — lançamentos bloqueados
        </div>
      )}
      {quinzena.status === "PAGA" && (
        <div className="mx-4 mt-3 bg-sucesso-suave border border-sucesso/30 rounded-xl px-3 py-2 text-xs text-sucesso flex items-center gap-2">
          <CreditCard className="h-3.5 w-3.5 shrink-0" />
          Quinzena paga
        </div>
      )}

      {/* §1 item 5 — Diaristas (accordion). O volume de gente e de dinheiro está
          aqui — é o que se olha primeiro ao abrir a tela (§2). */}
      <div className="px-4 mt-3 space-y-2">
        {funcData.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Nenhum lançamento nesta quinzena</div>
        ) : (
          funcData.map(({ func, diarias: fd, total, diasTrabalhados, eraDiarista }) => {
            const isOpen = openIds.has(func.id);
            const initials = avatarInitials(func.nome);
            const cor = avatarCor(func.id);
            // §5.4 — formato exato do card: Diárias · Colheita(adicional) · Recebe em.
            // §2.2 — uma linha por tipo (nunca soma COLHEITA com OUTRO antes de exibir).
            const adicionais = adicionalPorFuncionario.get(func.id);
            const totalComAdicional = total + somarLinhasAdicional(adicionais) / 100;
            return (
              <div key={func.id} className="bg-card rounded-xl border border-border overflow-hidden">
                <button
                  className="w-full flex flex-col gap-1.5 px-4 py-3 text-left"
                  onClick={() => toggleId(func.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: cor }}>
                      {initials}
                    </div>
                    <span className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground">{func.apelido ?? func.nome}</span>
                    {/* §3 — espelho do "era mensalista nesta quinzena" do bloco Mensalistas:
                        fato histórico (tem diária de trabalho aqui), regime de hoje já mudou. */}
                    {eraDiarista && (
                      <Badge variant="outline" className="bg-atencao-suave text-atencao border-atencao/30 text-[9px] shrink-0">
                        era diarista nesta quinzena
                      </Badge>
                    )}
                    {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                  </div>
                  <div className="pl-12 space-y-0.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Diárias · {diasTrabalhados} dia{diasTrabalhados !== 1 ? "s" : ""}</span>
                      <span className="font-medium text-foreground">{formatMoney(total)}</span>
                    </div>
                    {adicionais?.map(linha => (
                      <div key={`${linha.tipo}-${linha.sinal}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-sucesso">
                            {rotuloTipoAdicional(linha.tipo)} — {rotuloSinalLancamento(linha.sinal).toLowerCase()}
                          </span>
                          {/* Desconto nunca com "+": mesmo valor positivo, sentido oposto (046). */}
                          <span className={`font-medium ${linha.sinal === "DESCONTO" ? "text-erro" : "text-sucesso"}`}>
                            {linha.sinal === "DESCONTO" ? "−" : "+"} {formatarBRL(paraReais(linha.totalCentavos))}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {formatarResumoAdicional({
                            qtd: linha.qtd, kgTotal: linha.kgTotal,
                            totalValor: paraReais(linha.totalCentavos),
                            tipoLabel: "lançamento",
                          })} ›
                        </p>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs font-bold text-foreground border-t border-border pt-1 mt-1">
                      <span>Recebe em {quinzena.dataPagamento ? formatarDataCurta(quinzena.dataPagamento) : "—"}</span>
                      <span className="text-sucesso">{formatMoney(totalComAdicional)}</span>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border">
                    <button
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium text-info border-b border-border"
                      onClick={e => { e.stopPropagation(); onRecibo(func.id); }}
                      data-testid={`recibo-mobile-${func.id}`}
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      Recibo da quinzena
                    </button>
                    {fd.map(d => {
                      const tarefaStatus = diaStatusMap.get(`${func.id}|${d.data}`);
                      const isFaltou = tarefaStatus === "FALTOU";
                      const isPending = tarefaStatus === "PENDENTE";
                      const rowBg = isFaltou
                        ? "bg-erro-suave hover:bg-erro-suave"
                        : isPending
                        ? "bg-atencao-suave hover:bg-atencao-suave"
                        : "hover:bg-muted";
                      const valorColor = isFaltou
                        ? "font-medium text-erro"
                        : isPending
                        ? "font-medium text-atencao"
                        : "font-medium text-sucesso";
                      return (
                        <button
                          key={d.id}
                          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${rowBg}`}
                          onClick={() => onOpenDayPanel(func.id, func.apelido ?? func.nome, d.data)}
                        >
                          <span className="text-muted-foreground capitalize text-xs w-20 text-left">
                            {format(new Date(d.data + "T00:00:00"), "EEE dd/MM", { locale: ptBR })}
                          </span>
                          <span className="text-muted-foreground text-xs flex-1 min-w-0 text-left px-2 flex items-center gap-1">
                            {isPending && <AlertTriangle className="h-3 w-3 text-atencao shrink-0" />}
                            <span className="truncate">
                              {isFaltou ? "Faltou" : (ativsDoDia(func.id, d.data, d.tipo) || TIPO_LABELS[d.tipo] || d.tipo)}
                            </span>
                          </span>
                          <span className={valorColor}>{formatMoney(parseFloat(d.valor))}</span>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60 ml-2" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* §1 item 6 — Mensalistas e encarregados. Consulta, não operação diária:
          vem DEPOIS dos diaristas (§2). */}
      {mensalistasBlock && <div className="px-4 mt-4">{mensalistasBlock}</div>}

      {/* §1 item 7 — Adicionais (Fase 3). O pb-48 aqui (não no accordion) é o
          que deixa a barra fixa do rodapé livre por cima do ÚLTIMO bloco. */}
      <div className="px-4 mt-4 pb-48">{adicionaisPlaceholder}</div>

      {/* Filter bottom sheet */}
      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-base">Filtrar funcionários</SheetTitle>
          </SheetHeader>
          <div className="flex gap-2 mb-3">
            <button
              onClick={marcarTodos}
              className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-foreground active:bg-muted"
            >
              Marcar todos
            </button>
            <button
              onClick={limparTodos}
              className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-foreground active:bg-muted"
            >
              Desmarcar todos
            </button>
          </div>
          <div className="space-y-1 pb-6">
            {funcionarios.map(f => (
              <button
                key={f.id}
                onClick={() => toggleFunc(f.id)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl active:bg-muted transition-colors"
              >
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: avatarCor(f.id) }}>
                  {avatarInitials(f.nome)}
                </div>
                <span className="flex-1 text-left text-sm font-medium text-foreground">{f.apelido ?? f.nome}</span>
                <Checkbox
                  checked={isVisivel(f.id)}
                  onCheckedChange={() => toggleFunc(f.id)}
                  className="pointer-events-none"
                />
              </button>
            ))}
          </div>
          {filtrando && (
            <p className="text-xs text-atencao text-center pb-2">
              Mostrando {nVisiveis} de {funcionarios.length} funcionários · Fechar Quinzena fecha todos.
            </p>
          )}
        </SheetContent>
      </Sheet>

      {/* §4.4 — rodapé fixo: as três parcelas e o total que sai no dia do
          pagamento. As parcelas ficam visíveis de propósito; um número só
          esconde de onde veio o dinheiro. */}
      <div className="fixed bottom-[68px] left-0 right-0 bg-background border-t border-border px-4 py-2 z-30">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>diárias {formatarBRL(totalGeral)}</span>
          <span>adicionais {formatarBRL(totalAdicionaisReais)}</span>
          <span>adiantamentos {formatarBRL(totalAdiantamentos)}</span>
        </div>
        <p className="text-sm font-bold text-foreground">
          Sai em {quinzena.dataPagamento ? formatarDataCurta(quinzena.dataPagamento) : "—"}
          {" · "}{formatarBRL(totalASair)}
        </p>
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border px-4 py-3 flex gap-2 z-30">
        {isAdmin && quinzena.status === "ABERTA" && (
          <>
            <button
              onClick={onLancarDiaria}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3 text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Lançar diária
            </button>
            <button
              onClick={onFechar}
              className="flex items-center justify-center gap-2 border border-info/30 text-info rounded-xl py-3 px-4 text-sm font-medium"
            >
              <Lock className="h-4 w-4" />
              Fechar
            </button>
          </>
        )}
        {isAdmin && quinzena.status === "FECHADA" && (
          <button
            onClick={onPagar}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3 text-sm font-medium"
          >
            <CreditCard className="h-4 w-4" />
            Marcar como paga
          </button>
        )}
        {isAdmin && (
          <button
            onClick={onNovaQuinzena}
            className="flex items-center justify-center gap-2 border border-border text-muted-foreground rounded-xl py-3 px-4 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EquipeFinanceiro() {
  // PR 2: fechar/pagar quinzena e gerar é `equipe.folha.fechar` (ADMIN e GESTOR).
  const isAdmin = usePode("equipe.folha.fechar");
  const { data: quinzenas = [], isLoading: qsLoading } = useQuinzenas();
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: atividades = [] } = useAtividades();
  const updateTarefa = useUpdateTarefa();
  const qc = useQueryClient();
  const { data: setoresRaw = [] } = useQuery<{ id: string; nome: string; ativo: boolean }[]>({ queryKey: ["/api/setores"] });
  const setores = setoresRaw.filter(s => s.ativo !== false).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const [quinzenaIdx, setQuinzenaIdx] = useState(0);

  // ── Financeiro v3 (D-M1) — duas abas, estado na URL ─────────────────────────
  // `?aba=mes&competencia=2026-08` / `?aba=quinzena&quinzena=<id>`: é o que faz
  // o link da faixa "sai também nesta data" e o "voltar" do celular funcionarem.
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = new URLSearchParams(search);
  const aba: "quinzena" | "mes" = params.get("aba") === "mes" ? "mes" : "quinzena";
  const competenciaDaUrl = params.get("competencia");
  const quinzenaDaUrl = params.get("quinzena");
  const irPara = (proxima: "quinzena" | "mes", extra: Record<string, string | undefined> = {}, replace = false) => {
    const p = new URLSearchParams();
    p.set("aba", proxima);
    for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
    setLocation(`/equipe/financeiro?${p.toString()}`, { replace } as any);
  };
  // Navegar entre quinzenas grava o id na URL (replace, sem poluir o histórico).
  const navegarQuinzena = (idx: number) => {
    setQuinzenaIdx(idx);
    const q = quinzenas[idx];
    if (q) irPara("quinzena", { quinzena: q.id }, true);
  };
  useEffect(() => {
    if (!quinzenaDaUrl || quinzenas.length === 0) return;
    const idx = quinzenas.findIndex(q => q.id === quinzenaDaUrl);
    if (idx >= 0) setQuinzenaIdx(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quinzenaDaUrl, quinzenas.length]);

  // ── Funcionário filter (view-only) ──────────────────────────────────────────
  const [funcsExcluidos, setFuncsExcluidos] = useState<Set<string>>(new Set());
  // Busca por funcionário: NO CLIENTE, e dobrada na mesma noção de visibilidade
  // que o filtro do popover. Se ficasse só no render das linhas, a linha TOTAL
  // (que soma por `isVisivel`) continuaria somando quem sumiu da tela — dinheiro
  // visível sem linha que o explique. Termo vazio passa tudo, então sem busca
  // nenhum número muda de valor.
  const [busca, setBusca] = useState("");
  const nomeDoFunc = new Map(funcionarios.map(f => [f.id, `${f.nome} ${f.apelido ?? ""}`]));
  const isVisivel = (id: string) =>
    !funcsExcluidos.has(id) && combina(busca, nomeDoFunc.get(id));
  const toggleFunc = (id: string) =>
    setFuncsExcluidos(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const marcarTodos = () => setFuncsExcluidos(new Set());
  const limparTodos = () => setFuncsExcluidos(new Set(funcionarios.map(f => f.id)));
  const funcsVisiveis = funcionarios.filter(f => isVisivel(f.id));
  const nVisiveis = funcsVisiveis.length;
  const filtrando = funcionarios.some(f => !isVisivel(f.id));

  // Desktop dialogs
  const [fecharOpen, setFecharOpen] = useState(false);
  const [pagarOpen, setPagarOpen] = useState(false);

  // Shared sheets (mobile + desktop flows)
  const [novaQOpen, setNovaQOpen] = useState(false);
  const [diariaSheetMode, setDiariaSheetMode] = useState<"create" | "edit">("create");
  const [diariaSheetOpen, setDiariaSheetOpen] = useState(false);
  const [editingDiaria, setEditingDiaria] = useState<DiariaParaEditar | null>(null);
  const [editingTarefasDia, setEditingTarefasDia] = useState<TarefaParaEditar[]>([]);
  const [fechandoMobile, setFechandoMobile] = useState(false);
  const [pagarMobileOpen, setPagarMobileOpen] = useState(false);
  const [dayPanel, setDayPanel] = useState<{
    funcId: string;
    funcNome: string;
    dateStr: string;
  } | null>(null);

  const quinzena = quinzenas[quinzenaIdx];
  const { data: diariasBrutas = [] } = useDiariasByQuinzena(quinzena?.id);

  // ⚠️ PONTO DE SOMA DE FOLHA — choke point único da tela.
  //
  // A diária de registro do mensalista (tipo MENSALISTA, valor 0) é EXCLUÍDA AQUI,
  // POR TIPO, antes de qualquer soma ou contagem. Toda a tela (mobile e desktop,
  // total geral, total por funcionário, total do dia, contagem de dias, painel do
  // dia, lista de pagáveis) come deste array — filtrar aqui é o que garante que o
  // mensalista não aparece entre os pagáveis da quinzena.
  //
  // Filtrar por TIPO e não por valor é deliberado: as somas abaixo são
  // `reduce(+parseFloat(d.valor))` sem filtro nenhum, então uma linha MENSALISTA
  // com valor plantado no banco entraria no total como se fosse dia pago.
  const diarias = diariasBrutas.filter(d => d.tipo !== "MENSALISTA");

  // ── Fase 2 ────────────────────────────────────────────────────────────────
  // O bloco de mensalistas vem de ENDPOINT PRÓPRIO (§4.5), nunca do array acima:
  // é o que garante que o mensalista não tem caminho até o total pago da quinzena.
  const { data: mensalistas = [] } = useMensalistasDaQuinzena(quinzena?.id);
  const { data: organizacao } = useOrganizacao();

  // ── Fase 3 — pagamento adicional na tarefa ──────────────────────────────────
  // Mesmo padrão do bloco de mensalistas: endpoint próprio (§4.5), nunca
  // misturado no array de `diarias` — o adicional não tem caminho implícito
  // até a folha de diária.
  const { data: adicionaisQuinzena } = useAdicionaisDaQuinzena(quinzena?.id);
  // D-M4 (Financeiro v3) — o que de `tarefa_adicional` sai no CAIXA desta
  // quinzena. Desconto de MENSALISTA liquida no fechamento do mês (semeado em
  // `fechamento_mensal.descontos`) e NÃO pode abater aqui também — era dupla
  // contagem. Filtra UMA vez, na origem; tudo abaixo (card, TOTAL, "Sai em",
  // bloco Adicionais) come deste array.
  const agregadoNoCaixa = lancamentosNoCaixaDaQuinzena(adicionaisQuinzena?.agregado ?? []);
  const detalheNoCaixa = lancamentosNoCaixaDaQuinzena(adicionaisQuinzena?.detalhe ?? []);
  // §2.2 — uma linha por (pessoa, tipo): nunca soma COLHEITA e OUTRO juntos
  // antes de exibir, senão o R$/kg médio do resumo perde o sentido.
  const adicionalPorFuncionario = agruparAdicionaisPorFuncionario(agregadoNoCaixa);
  // `netLancamentosCentavos` e não `+ paraCentavos(a.total)`: depois da 046 a
  // mesma tabela guarda DESCONTO, e somar o valor cru transformaria um abatimento
  // em dinheiro A MAIS — sinal invertido num número que vira pagamento.
  const totalAdicionaisCentavos = netLancamentosCentavos(
    agregadoNoCaixa
      .filter((a: AdicionalAgregado) => isVisivel(a.funcionario_id))
      .map((a: AdicionalAgregado) => ({ sinal: a.sinal, valor: a.total })),
  );
  const totalAdicionaisReais = paraReais(totalAdicionaisCentavos);
  const definirDataPagamento = useDefinirDataPagamento();
  const [adiantamentosOpen, setAdiantamentosOpen] = useState(false);
  const [fechamentoOpen, setFechamentoOpen] = useState(false);
  const { data: tarefasPeriodo = [] } = useTarefasByPeriodo(
    quinzena?.periodoInicio ?? "",
    quinzena?.periodoFim ?? ""
  );

  // Mapa de status dominante por (funcionarioId|data): FALTOU > PENDENTE > EM_ANDAMENTO > CONCLUIDA
  // Mantido para detectar FALTOU sem diária (row visibility) e para openEditDiaria
  const STATUS_PRIO: Record<string, number> = { FALTOU: 4, PENDENTE: 3, EM_ANDAMENTO: 2, CANCELADA: 1, CONCLUIDA: 0 };
  const diaStatusMap = new Map<string, string>();
  for (const t of tarefasPeriodo) {
    const key = `${t.funcionarioId}|${t.data}`;
    const cur = diaStatusMap.get(key);
    if (!cur || (STATUS_PRIO[t.status] ?? 0) > (STATUS_PRIO[cur] ?? 0)) {
      diaStatusMap.set(key, t.status);
    }
  }

  // Status da célula financeira: derivado SOMENTE das tarefas vinculadas às diárias do dia
  // (tarefas de planejamento sem diária/valor não afetam a cor da célula)
  const tarefaStatusById = new Map(tarefasPeriodo.map(t => [t.id, t.status]));
  const diaCelulaStatusMap = new Map<string, "FALTOU" | "PENDENTE" | "CONCLUIDA">();
  for (const d of diarias) {
    const key = `${d.funcionarioId}|${d.data}`;
    if (!diaCelulaStatusMap.has(key)) {
      const lancamentos = diarias.filter(x => x.funcionarioId === d.funcionarioId && x.data === d.data);
      const linked = (lancamentos
        .map(x => (x as any).tarefaId ? tarefaStatusById.get((x as any).tarefaId) : null)
        .filter(s => s != null)) as string[];
      const diaTemFaltou = tarefasPeriodo.some(
        t => t.funcionarioId === d.funcionarioId && t.data === d.data && t.status === "FALTOU"
      );
      diaCelulaStatusMap.set(
        key,
        linked.includes("FALTOU") || (lancamentos.length === 0 && diaTemFaltou)
          ? "FALTOU"
          : linked.some(s => s === "PENDENTE" || s === "EM_ANDAMENTO")
          ? "PENDENTE"
          : "CONCLUIDA"
      );
    }
  }
  // Marcar dias FALTOU sem diária (ex.: faltou sem lançamento registrado)
  diaStatusMap.forEach((status, key) => {
    if (status === "FALTOU" && !diaCelulaStatusMap.has(key)) {
      diaCelulaStatusMap.set(key, "FALTOU");
    }
  });

  const dias = quinzena
    ? eachDayOfInterval({
        start: new Date(quinzena.periodoInicio + "T00:00:00"),
        end: new Date(quinzena.periodoFim + "T00:00:00"),
      })
    : [];

  // ── Totais da Fase 2 (DERIVADOS, nunca gravados) ──────────────────────────
  //
  // ⚠️ `quinzenas.total_bruto` continua sendo só a folha do diarista e NÃO é
  // tocado: é o histórico congelado das quinzenas fechadas, e mudar o que ele
  // significa reescreveria o passado. O "total a sair" abaixo é de exibição.
  const diasISO = dias.map(d => format(d, "yyyy-MM-dd"));
  // MESMA função que calcTotalQuinzena usa no servidor (shared/equipe-formula.ts)
  // — bruto − descontos − adiantamentos, não a soma bruta de `valor` de todos os
  // tipos. DESCONTO/ADIANTAMENTO de diarista são gravados como magnitude
  // POSITIVA; somar sem subtrair inflava "Sai em" em vez de abater (achado do
  // PR #122).
  const totalFolhaDiaristas = paraCentavos(
    calcFormula(diarias.filter(d => isVisivel(d.funcionarioId))).liquido
  );
  // Mesma função usada pela prévia do modal "Fechar Quinzena" (v2.10) — garante
  // os dois totais baterem por construção, não por coincidência do dado do dia.
  const totalAdiantamentos = totalAdiantamentosMensalistasCentavos(mensalistas);
  const totalASair = calcTotalASairQuinzena({
    folhaDiaristas: paraReais(totalFolhaDiaristas),
    adicionais: totalAdicionaisReais,
    adiantamentos: paraReais(totalAdiantamentos),
  });

  // D11 (v2.7) — a competência vem do mês de INÍCIO da quinzena EM EXIBIÇÃO,
  // nunca de "new Date()" nem da data de pagamento. Não depende de
  // `dataPagamento` estar definida — só de existir uma quinzena selecionada —
  // e é por isso que o cartão muda sozinho ao navegar: `quinzena` muda,
  // `competenciaCard` muda junto.
  const competenciaCard = quinzena ? competenciaDaQuinzena(quinzena) : null;
  // `competenciaAtual` é só o default do DIÁLOGO de gerar fechamento (um campo
  // de formulário, não um dado que afirma ser verdade) — aí "mês corrente" é um
  // chute aceitável para o usuário corrigir, diferente do cartão.
  const competenciaAtual = competenciaCard ?? hojeNaFazenda().slice(0, 7);
  // Competência da aba Mês: a da URL (vinda da faixa ou do ‹ › da aba), senão a
  // da quinzena EM EXIBIÇÃO — a aba e a faixa levam ao mesmo mês —, e só sem
  // quinzena nenhuma o mês corrente NA FAZENDA (nunca `toISOString()`: das 21h
  // às 23h59 na Bahia isso é amanhã). Declarada DEPOIS de `competenciaCard`, que
  // é a fonte única (D11) — `const` lido antes da declaração é TDZ em runtime.
  const competenciaMes = competenciaDaUrl && COMPETENCIA_RE.test(competenciaDaUrl)
    ? competenciaDaUrl
    : competenciaAtual;

  const { data: fechamentosDoMes = [] } = useFechamentos(competenciaCard ?? undefined);
  // §3 — Σ dos fechamentos ABERTO da competência. Só cai pra projeção quando
  // NENHUM fechamento foi gerado ainda pra esse mês (nem aberto, nem fechado) —
  // se já existe fechamento e todos estão FECHADOS, o valor real É zero (nada
  // mais a sair), não uma reestimativa.
  // Financeiro v3: UMA função (shared) para o cartão E para a faixa "sai também".
  const existeFechamentoNoMes = fechamentosDoMes.length > 0;
  const salariosDoMes = totalSalariosDoMesCentavos({ fechamentos: fechamentosDoMes, mensalistas });
  const totalFechamentoMes = paraReais(salariosDoMes.centavos);

  // D-M2 — a faixa "Nesta data de pagamento sai também": o único lugar em que
  // o mensalista aparece na aba Quinzena (adiantamento + adicionais dele +
  // link para os salários do mês). Só quando houver algo.
  const faixaSaiJunto = quinzena && competenciaCard ? (
    <SaiJuntoNestaData
      mensalistas={mensalistas}
      agregadoMensalistas={agregadoNoCaixa.filter((a: AdicionalAgregado) => a.tipo_remuneracao === "MENSALISTA")}
      competencia={competenciaCard}
      salariosCentavos={salariosDoMes.centavos}
      salariosPrevisto={salariosDoMes.previsto}
      dataFechamento={dataPagamentoDaCompetencia(proximaCompetencia(competenciaCard), organizacao?.diaPagamentoMensal ?? null)}
      onIrParaMes={() => irPara("mes", { competencia: competenciaCard })}
    />
  ) : null;

  // §1 itens 3-4 — cartões de data + ações administrativas na mesma faixa,
  // logo abaixo do período (nunca junto do bloco de mensalistas — §2).
  const cartoesDeData = quinzena ? (
    <CartoesDeData
      periodoInicio={quinzena.periodoInicio}
      dataPagamento={quinzena.dataPagamento ?? null}
      totalASair={totalASair}
      totalFechamentoMes={totalFechamentoMes}
      totalFechamentoMesPrevisto={!existeFechamentoNoMes}
      diaPagamentoMensal={organizacao?.diaPagamentoMensal ?? null}
      podeDefinirData={isAdmin && quinzena.status === "ABERTA"}
      salvando={definirDataPagamento.isPending}
      onDefinirData={iso => definirDataPagamento.mutate({ id: quinzena.id, dataPagamento: iso })}
    />
  ) : null;

  // Gerar adiantamentos / Fechamento do mês — reaproveitado em duas faixas
  // diferentes (§1 item 4): no mobile, empilhado com os cartões, sem "Lançar
  // Diária" (que fica na barra fixa do rodapé); no desktop, na MESMA linha do
  // "Lançar Diária", inline (`className` aqui é neutro de propósito).
  const acoesSecundarias = quinzena && isAdmin ? (
    <>
      <Button size="sm" variant="outline"
        disabled={quinzena.status !== "ABERTA"}
        onClick={() => setAdiantamentosOpen(true)}>
        Gerar adiantamentos
      </Button>
      <Button size="sm" variant="outline" onClick={() => setFechamentoOpen(true)}>
        Fechamento do mês
      </Button>
    </>
  ) : null;

  // Composição do mobile: cartões + ações administrativas empilhados no topo
  // (§1 itens 3-4). "Lançar Diária" fica de fora — sticky bottom bar (mobile).
  const cartoesEAcoes = quinzena ? (
    <div className="space-y-3">
      {cartoesDeData}
      {faixaSaiJunto}
      {acoesSecundarias && <div className="flex gap-2 [&>*]:flex-1 [&>*]:text-xs">{acoesSecundarias}</div>}
    </div>
  ) : null;

  // §1 item 6 — Mensalistas e encarregados, SEPARADO dos cartões: precisa
  // renderizar depois do bloco Diaristas (§2), não junto no topo.
  const mensalistasBlockContent = quinzena ? (
    <BlocoMensalistas
      linhas={mensalistas}
      dias={diasISO}
      periodoInicio={quinzena.periodoInicio}
      diaPagamentoMensal={organizacao?.diaPagamentoMensal ?? null}
      adicionalPorFuncionario={adicionalPorFuncionario}
    />
  ) : null;

  // §1 item 7 / §5.3 — Adicionais (tarefa_adicional, Fase 3): detalhe por
  // lançamento (funcionário, tarefa, data, kg, valor) + subtotal. Filtra pelo
  // mesmo `isVisivel` da tela — o filtro de funcionário vale para tudo.
  const detalheAdicionaisVisiveis = detalheNoCaixa.filter(d => isVisivel(d.funcionarioId));
  const adicionaisPlaceholder = detalheAdicionaisVisiveis.length === 0 ? (
    <div className="rounded-xl border border-dashed border-border bg-muted px-3 py-2.5 text-xs text-muted-foreground">
      <p className="font-medium text-muted-foreground">Adicionais</p>
      <p>— nenhum lançamento nesta quinzena</p>
    </div>
  ) : (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-3 py-2 bg-muted border-b border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Adicionais</p>
      </div>
      <div className="divide-y divide-border">
        {detalheAdicionaisVisiveis.map(d => {
          const tarefa = tarefasPeriodo.find(t => t.id === d.tarefaId);
          const atividadeNome = tarefa ? atividades.find(a => a.id === (tarefa as any).atividadeId)?.nome : undefined;
          return (
            <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">{d.apelido ?? d.nome}</p>
                <p className="text-muted-foreground truncate">
                  {atividadeNome ?? (d.descricao || "adicional")}
                  {" · "}{format(new Date(d.data + "T00:00:00"), "dd/MM")}
                  {d.kg ? ` · ${formatarKg(d.kg)}` : ""}
                </p>
              </div>
              {/* Desconto nunca com "+": mesmo valor positivo, sentido oposto (046). */}
              <span className={`font-semibold shrink-0 ${d.sinal === "DESCONTO" ? "text-erro" : "text-sucesso"}`}>
                {d.sinal === "DESCONTO" ? "−" : "+"} {formatarBRL(d.valor)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between px-3 py-2 bg-muted border-t border-border text-xs font-bold text-foreground">
        <span>Subtotal</span>
        <span>{formatarBRL(totalAdicionaisReais)}</span>
      </div>
    </div>
  );

  const openCreateDiaria = () => {
    setDiariaSheetMode("create");
    setEditingDiaria(null);
    setDiariaSheetOpen(true);
  };

  const openEditDiaria = (d: Diaria, funcNome: string) => {
    const dominantStatus = diaStatusMap.get(`${d.funcionarioId}|${d.data}`) as import("@/components/equipe/TarefaDiariaSheet").TarefaStatus | undefined;
    setEditingTarefasDia([]); // Fix B: evitar estado cruzado
    setEditingDiaria({
      id: d.id,
      quinzenaId: d.quinzenaId,
      data: d.data,
      funcionarioId: d.funcionarioId,
      funcionarioNome: funcNome,
      tipo: d.tipo,
      valor: d.valor,
      valorAdicional: (d as any).valorAdicional ?? null,
      comentario: (d as any).comentario ?? null,
      tarefaId: (d as any).tarefaId ?? null,
      status: dominantStatus ?? "PENDENTE",
    });
    setDiariaSheetMode("edit");
    setDiariaSheetOpen(true);
  };

  const closeDiariaSheet = () => {
    setDiariaSheetOpen(false);
    setEditingDiaria(null);
    setEditingTarefasDia([]);
  };

  const openEditDia = (funcId: string, funcNome: string, dateStr: string) => {
    setEditingDiaria(null); // Fix B: evitar estado cruzado
    const tarefasDoDia = tarefasPeriodo.filter(t => t.funcionarioId === funcId && t.data === dateStr);
    const mapped: TarefaParaEditar[] = tarefasDoDia.map(t => ({
      id: t.id,
      data: t.data,
      funcionarioId: t.funcionarioId,
      funcionarioNome: funcNome,
      atividadeId: (t as any).atividadeId ?? "",
      atividadeNome: atividades.find(a => a.id === (t as any).atividadeId)?.nome ?? "—",
      setorId: (t as any).setorId ?? null,
      setorIds: Array.isArray((t as any).setorIds) && (t as any).setorIds.length > 0
        ? (t as any).setorIds
        : ((t as any).setorId ? [(t as any).setorId] : []),
      setorNome: "",
      tipo: (t as any).tipo ?? "NORMAL",
      status: t.status as import("@/components/equipe/TarefaDiariaSheet").TarefaStatus,
      valor: String(parseFloat(String((t as any).valor ?? 0))),
      valorAdicional: null,
      comentario: (t as any).comentario ?? null,
    }));
    setEditingTarefasDia(mapped);
    setDiariaSheetMode("edit");
    setDiariaSheetOpen(true);
  };

  // ── Recibo da quinzena (diarista) ──────────────────────────────────────────
  // Texto montado em shared/equipe-recibo.ts com os MESMOS números da tela:
  // `calcFormula` para a folha e o agregado do caixa (D-M4) para os
  // lançamentos. Nada é recalculado aqui.
  const [reciboFuncId, setReciboFuncId] = useState<string | null>(null);
  const funcDoRecibo = funcionarios.find(f => f.id === reciboFuncId) ?? null;
  const atividadeNomePorId = new Map(atividades.map(a => [a.id, a.nome]));
  /** Atividades daquele dia e tipo — a mesma leitura do card do celular. */
  const atividadesDoDia = (funcId: string, data: string, tipo: string): string => {
    const nomes = tarefasPeriodo
      .filter(t => t.funcionarioId === funcId && t.data === data && ((t as any).tipo ?? "NORMAL") === tipo)
      .map(t => atividadeNomePorId.get((t as any).atividadeId ?? ""))
      .filter((n): n is string => !!n);
    return Array.from(new Set(nomes)).join(", ");
  };
  const textoRecibo = funcDoRecibo && quinzena
    ? montarReciboQuinzena({
        organizacao: organizacao?.nome ?? "Fazenda",
        pessoa: { nome: funcDoRecibo.nome, apelido: funcDoRecibo.apelido },
        quinzena: {
          periodoInicio: quinzena.periodoInicio,
          periodoFim: quinzena.periodoFim,
          dataPagamento: quinzena.dataPagamento ?? null,
          status: quinzena.status,
        },
        diarias: diarias
          .filter(d => d.funcionarioId === funcDoRecibo.id)
          .map(d => ({ data: d.data, tipo: d.tipo, valor: d.valor, descricao: atividadesDoDia(funcDoRecibo.id, d.data, d.tipo) })),
        lancamentos: detalheNoCaixa
          .filter(l => l.funcionarioId === funcDoRecibo.id)
          .map(l => ({ data: l.data, sinal: l.sinal ?? "ADICIONAL", descricao: l.descricao, valor: l.valor })),
        geradoEm: hojeNaFazenda(),
      })
    : "";

  const openDayPanel = (func: Funcionario, dateStr: string) => {
    setDayPanel({ funcId: func.id, funcNome: func.apelido ?? func.nome, dateStr });
  };
  const closeDayPanel = () => setDayPanel(null);

  if (qsLoading) return <div className="p-6"><PageSkeleton variante="tabela" linhas={6} /></div>;

  return (
    <>
      {/* Um cabeçalho só, acima dos dois ramos. Antes havia dois títulos
          "Financeiro" — um `text-lg` no topbar do celular, outro `text-2xl` no
          desktop —, ambos no DOM ao mesmo tempo. O PageHeader já é responsivo. */}
      <div className="px-4 pt-4 md:px-6 md:pt-6">
        <PageHeader
          titulo="Financeiro"
          acao={
            aba === "quinzena" && quinzena ? (
              <StatusBadge tom={STATUS_TOM[quinzena.status] ?? "neutro"} ponto>
                {quinzena.status}
              </StatusBadge>
            ) : undefined
          }
        />
        {/* D-M1 — troca PÁGINA DENTRO DA PÁGINA: dois calendários, duas abas. */}
        <PageTabs
          className="mt-3"
          abas={ABAS_FINANCEIRO}
          ativa={aba}
          onChange={id => irPara(
            id === "mes" ? "mes" : "quinzena",
            id === "mes" ? { competencia: competenciaMes } : { quinzena: quinzena?.id },
          )}
        />
      </div>

      {aba === "mes" ? (
        <div className="px-4 pt-4 md:px-6 md:pt-4">
          <FinanceiroMes
            competencia={competenciaMes}
            onCompetencia={c => irPara("mes", { competencia: c }, true)}
          />
        </div>
      ) : (
      <>
      {/* ── Mobile layout ── */}
      <div className="block md:hidden">
        <MobileFinanceiro
          quinzenas={quinzenas}
          quinzenaIdx={quinzenaIdx}
          setQuinzenaIdx={navegarQuinzena}
          quinzena={quinzena}
          diarias={diarias}
          funcionarios={funcionarios}
          isAdmin={isAdmin}
          diaStatusMap={diaCelulaStatusMap}
          onLancarDiaria={openCreateDiaria}
          onFechar={() => setFechandoMobile(true)}
          onPagar={() => setPagarMobileOpen(true)}
          onNovaQuinzena={() => setNovaQOpen(true)}
          onOpenDayPanel={(funcId, funcNome, dateStr) => setDayPanel({ funcId, funcNome, dateStr })}
          onRecibo={setReciboFuncId}
          atividades={atividades}
          tarefas={tarefasPeriodo}
          funcsExcluidos={funcsExcluidos}
          isVisivel={isVisivel}
          toggleFunc={toggleFunc}
          marcarTodos={marcarTodos}
          limparTodos={limparTodos}
          nVisiveis={nVisiveis}
          filtrando={filtrando}
          cartoesEAcoes={cartoesEAcoes}
          mensalistasBlock={mensalistasBlockContent}
          adicionaisPlaceholder={adicionaisPlaceholder}
          totalASair={totalASair}
          totalAdiantamentos={paraReais(totalAdiantamentos)}
          adicionalPorFuncionario={adicionalPorFuncionario}
          totalAdicionaisReais={totalAdicionaisReais}
        />
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden md:block px-6 pt-4 pb-6 space-y-6">
        {/* §1 item 1 — navegação de quinzena logo abaixo do cabeçalho, sempre no
            topo, como em qualquer outra tela do módulo. O bug era o inverso: o
            conteúdo da Fase 2 (cartões/ações/mensalistas) vinha ANTES disto.
            O título saiu daqui: é um só, no PageHeader acima dos dois ramos. */}
        <PageToolbar>
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={() => navegarQuinzena(Math.min(quinzenaIdx + 1, quinzenas.length - 1))}
            disabled={quinzenaIdx >= quinzenas.length - 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="shrink-0" onClick={() => navegarQuinzena(0)}>Atual</Button>
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={() => navegarQuinzena(Math.max(quinzenaIdx - 1, 0))}
            disabled={quinzenaIdx <= 0}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button variant="outline" className="shrink-0" onClick={() => setNovaQOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />Quinzena
            </Button>
          )}
        </PageToolbar>

        {!quinzena ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>Nenhuma quinzena encontrada.</p>
            {isAdmin && (
              <Button className="mt-4" onClick={() => setNovaQOpen(true)}>Criar Quinzena</Button>
            )}
          </div>
        ) : (
          <>
            {/* §1 item 2 — período */}
            <p className="text-sm text-muted-foreground">
              {format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM/yyyy")}
              {" – "}
              {format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM/yyyy")}
            </p>

            {/* §1 item 3 — cartões de data */}
            {cartoesDeData}
            {faixaSaiJunto}

            {/* §1 item 4 — ações, todas na mesma faixa: Lançar Diária,
                Gerar adiantamentos, Fechamento do mês. */}
            <div className="flex flex-wrap items-center gap-2">
              {quinzena.status === "ABERTA" && (
                <Button size="sm" onClick={openCreateDiaria}>
                  <Plus className="h-4 w-4 mr-2" />Lançar Diária
                </Button>
              )}
              {acoesSecundarias}
            </div>

            <div className="flex items-center gap-2">
              <PageSearch
                valor={busca}
                onChange={setBusca}
                placeholder="Funcionário"
                testId="input-busca-financeiro"
              />
              {/* Funcionário filter popover — filtro da grade de diaristas logo
                  abaixo, fica junto dela, fora da faixa de ações administrativas. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={filtrando ? "border-sucesso/30 text-sucesso bg-sucesso-suave" : ""}>
                    <Users className="h-4 w-4 mr-1.5" />
                    Funcionários{" "}
                    <span className={`ml-1 font-bold ${filtrando ? "text-sucesso" : ""}`}>
                      {nVisiveis}/{funcionarios.length}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Funcionários</span>
                    <div className="flex gap-2">
                      <button onClick={marcarTodos} className="text-xs text-info hover:underline">Todos</button>
                      <button onClick={limparTodos} className="text-xs text-muted-foreground hover:underline">Nenhum</button>
                    </div>
                  </div>
                  <div className="space-y-0.5 max-h-64 overflow-y-auto">
                    {funcionarios.map(f => (
                      <label
                        key={f.id}
                        className="flex items-center gap-2 px-1 py-1.5 rounded-md hover:bg-muted cursor-pointer"
                      >
                        <Checkbox
                          checked={isVisivel(f.id)}
                          onCheckedChange={() => toggleFunc(f.id)}
                        />
                        <span className="text-sm truncate">{f.apelido ?? f.nome}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* §4.2 bloco 1 — Diaristas. Grade de sempre, agora com as três
                colunas à direita (Diárias · Adicionais · Total). */}
            <h2 className="text-sm font-bold text-sucesso">Diaristas</h2>
            <div className="overflow-x-auto rounded-lg border">
              <DataTable>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead className="sticky left-0 bg-background z-10 min-w-[140px]">Funcionário</DataTableHead>
                    {dias.map(d => (
                      <DataTableHead key={d.toISOString()} className="text-center min-w-[52px] px-1">
                        <div className="text-[10px] font-normal text-muted-foreground uppercase">
                          {format(d, "EEE", { locale: ptBR }).replace(".", "")}
                        </div>
                        <div className="text-xs font-medium">{format(d, "dd/MM")}</div>
                      </DataTableHead>
                    ))}
                    <DataTableHead className="text-right min-w-[44px]">Dias</DataTableHead>
                    <DataTableHead className="text-right min-w-[90px]">Diárias</DataTableHead>
                    {/* Fase 3 (`tarefa_adicional`). A coluna já existe para a tela
                        não precisar ser reescrita quando os adicionais chegarem. */}
                    <DataTableHead className="text-right min-w-[80px]">Adicionais</DataTableHead>
                    <DataTableHead className="text-right min-w-[90px]">Total</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {/* §3 (fix pós-#116) — a entrada é o FATO HISTÓRICO (tem diária
                      tipo!=MENSALISTA nesta quinzena), não o regime ATUAL. Filtrar
                      a lista inteira por `!ehMensalista(func)` (como antes) sumia
                      com quem trocou de regime DEPOIS de ter diárias reais — o
                      dinheiro continuava no TOTAL (que soma por tipo, não por
                      regime), só a linha desaparecia (o bug do Erivelton).
                      O fallback `hasFaltouDia` continua condicionado ao regime
                      ATUAL (não ao histórico) de propósito: é o que evita
                      reintroduzir o bug do "0 vermelho" do Dáu (mensalista, tarefa
                      FALTOU sem nenhuma diária de trabalho — essa falta pertence
                      ao bloco de Mensalistas, não a este). */}
                  {funcionarios.map(func => {
                    if (!isVisivel(func.id)) return null;
                    const dFunc = diarias.filter(d => d.funcionarioId === func.id);
                    // Dias efetivamente trabalhados: excluir FALTOU
                    const diasSet = new Set(
                      dFunc.filter(d => d.tipo === "NORMAL" && parseFloat(d.valor) > 0).map(d => d.data)
                    );
                    const total = dFunc.reduce((acc, d) => acc + parseFloat(d.valor), 0);
                    // Mostrar linha se tem diária, ou se há FALTOU e a pessoa
                    // ainda não é mensalista hoje (senão a falta é do outro bloco).
                    const hasFaltouDia = dias.some(d => diaCelulaStatusMap.get(`${func.id}|${format(d, "yyyy-MM-dd")}`) === "FALTOU");
                    const eraDiarista = ehMensalista(func);
                    if (!apareceComoDiarista(func, dFunc.length > 0, hasFaltouDia)) return null;
                    const adicionaisFunc = adicionalPorFuncionario.get(func.id);
                    const totalComAdicional = total + somarLinhasAdicional(adicionaisFunc) / 100;
                    return (
                      <DataTableRow key={func.id}>
                        <DataTableCell className="sticky left-0 bg-background font-medium text-sm">
                          <span className="truncate">{func.apelido ?? func.nome}</span>
                          {/* Recibo da pessoa — ícone na célula da IDENTIDADE, não
                              numa coluna nova: a grade de dias já é larga. */}
                          <button
                            type="button"
                            onClick={() => setReciboFuncId(func.id)}
                            title={`Recibo da quinzena — ${func.apelido ?? func.nome}`}
                            aria-label={`Recibo da quinzena de ${func.apelido ?? func.nome}`}
                            className="ml-1.5 align-middle text-muted-foreground hover:text-info"
                            data-testid={`recibo-${func.id}`}
                          >
                            <Share2 className="h-3.5 w-3.5 inline" />
                          </button>
                          {eraDiarista && (
                            <Badge variant="outline" className="ml-1.5 bg-atencao-suave text-atencao border-atencao/30 text-[9px] align-middle">
                              era diarista nesta quinzena
                            </Badge>
                          )}
                        </DataTableCell>
                        {dias.map(d => {
                          const dStr = format(d, "yyyy-MM-dd");
                          const lancamentos = dFunc.filter(di => di.data === dStr);
                          const valorDia = lancamentos.reduce((acc, di) => acc + parseFloat(di.valor), 0);
                          const celulaStatus = diaCelulaStatusMap.get(`${func.id}|${dStr}`);
                          const isFaltou = celulaStatus === "FALTOU";
                          const isPending = celulaStatus === "PENDENTE";
                          const showCell = lancamentos.length > 0 || isFaltou;
                          const displayVal = isFaltou && valorDia === 0
                            ? "0"
                            : valorDia % 1 === 0 ? valorDia.toFixed(0) : valorDia.toFixed(2);
                          const cellColor = isFaltou
                            ? "bg-erro-suave text-erro border-erro/30"
                            : isPending
                            ? "bg-atencao-suave text-atencao border-atencao/30"
                            : "bg-sucesso-suave text-sucesso border-sucesso/30";
                          return (
                            <DataTableCell key={dStr} className="text-center px-1 py-1.5">
                              {showCell ? (
                                <button
                                  title={
                                    isFaltou ? "Faltou" :
                                    isPending ? "⚠ Tarefa pendente — clique para ver detalhes" :
                                    lancamentos.length > 0 ? "Clique para ver detalhes" :
                                    `R$ ${valorDia.toFixed(2)}`
                                  }
                                  onClick={() => openDayPanel(func, dStr)}
                                  className={`text-xs font-medium px-1 py-0.5 rounded w-full flex items-center justify-center gap-0.5 border transition-colors cursor-pointer hover:bg-info-suave hover:text-info hover:border-info/30 ${cellColor}`}
                                >
                                  {isPending && <AlertTriangle className="h-2.5 w-2.5 shrink-0" />}
                                  {displayVal}
                                </button>
                              ) : (
                                <span className="block text-muted-foreground/20 text-xs text-center">—</span>
                              )}
                            </DataTableCell>
                          );
                        })}
                        <DataTableCell className="text-right text-sm">{diasSet.size}</DataTableCell>
                        <DataTableCell className="text-right text-sm">
                          R$ {total % 1 === 0 ? total.toFixed(0) : total.toFixed(2)}
                        </DataTableCell>
                        <DataTableCell className="text-right text-sm">
                          {adicionaisFunc?.length ? (
                            <div className="space-y-1">
                              {adicionaisFunc.map(linha => (
                                <div key={`${linha.tipo}-${linha.sinal}`}>
                                  <span className="font-medium text-sucesso">
                                    {linha.sinal === "DESCONTO" ? "−" : "+"} {formatarBRL(paraReais(linha.totalCentavos))}
                                  </span>
                                  <p className="text-[10px] text-muted-foreground truncate">
                                    {formatarResumoAdicional({
                                      qtd: linha.qtd, kgTotal: linha.kgTotal,
                                      totalValor: paraReais(linha.totalCentavos),
                                      tipoLabel: "lançamento",
                                    })}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </DataTableCell>
                        {/* O total soma diárias + adicionais, mas as PARCELAS ficam
                            visíveis ao lado — colapsar num número só esconde de
                            onde veio o dinheiro. */}
                        <DataTableCell className="text-right text-sm font-semibold">
                          R$ {totalComAdicional % 1 === 0 ? totalComAdicional.toFixed(0) : totalComAdicional.toFixed(2)}
                        </DataTableCell>
                      </DataTableRow>
                    );
                  })}
                  {/* Totals row */}
                  <DataTableRow className="font-bold bg-muted/50 border-t-2">
                    <DataTableCell className="sticky left-0 bg-muted/50 text-sm">
                      {filtrando ? (
                        <span className="flex items-center gap-1">
                          TOTAL
                          <span className="text-[10px] font-normal text-atencao ml-1">({nVisiveis}/{funcionarios.length})</span>
                        </span>
                      ) : "TOTAL"}
                    </DataTableCell>
                    {dias.map(d => {
                      const dStr = format(d, "yyyy-MM-dd");
                      const totalDia = diarias
                        .filter(di => di.data === dStr && isVisivel(di.funcionarioId))
                        .reduce((acc, di) => acc + parseFloat(di.valor), 0);
                      return (
                        <DataTableCell key={dStr} className="text-center px-1 py-1.5 text-xs font-semibold">
                          {totalDia > 0
                            ? (totalDia % 1 === 0 ? totalDia.toFixed(0) : totalDia.toFixed(2))
                            : <span className="text-muted-foreground/30">—</span>}
                        </DataTableCell>
                      );
                    })}
                    <DataTableCell />
                    <DataTableCell className="text-right text-sm">
                      R$ {(() => {
                        const t = diarias.filter(d => isVisivel(d.funcionarioId)).reduce((a, d) => a + parseFloat(d.valor), 0);
                        return t % 1 === 0 ? t.toFixed(0) : t.toFixed(2);
                      })()}
                    </DataTableCell>
                    <DataTableCell className="text-right text-sm font-medium text-sucesso">
                      {totalAdicionaisCentavos > 0 ? `+ ${formatarBRL(totalAdicionaisReais)}` : <span className="text-muted-foreground">—</span>}
                    </DataTableCell>
                    <DataTableCell className="text-right text-sm font-bold">
                      R$ {(() => {
                        const t = diarias.filter(d => isVisivel(d.funcionarioId)).reduce((a, d) => a + parseFloat(d.valor), 0) + totalAdicionaisCentavos / 100;
                        return t % 1 === 0 ? t.toFixed(0) : t.toFixed(2);
                      })()}
                    </DataTableCell>
                  </DataTableRow>
                </DataTableBody>
              </DataTable>
            </div>

            {/* Filter warning */}
            {filtrando && (
              <p className="text-xs text-atencao flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Mostrando {nVisiveis} de {funcionarios.length} funcionários — total filtrado.
                {" "}<button onClick={marcarTodos} className="underline underline-offset-2 font-medium">Ver todos</button>
                {" "}· Fechar Quinzena fecha todos.
              </p>
            )}

            {/* §1 item 6 — Mensalistas e encarregados: consulta, não operação
                diária, por isso vem DEPOIS dos diaristas (§2). */}
            {mensalistasBlockContent}

            {/* §1 item 7 — Adicionais (Fase 3, vazio por enquanto). */}
            {adicionaisPlaceholder}

            {/* §1 item 8 — caixa de total: mesma informação do rodapé fixo do
                mobile, sem sticky (desktop não precisa — a tela inteira já
                cabe na viewport na maioria dos casos). */}
            <div className="rounded-xl border bg-muted/30 px-4 py-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>diárias {formatarBRL(paraReais(totalFolhaDiaristas))}</span>
                <span>adicionais {formatarBRL(totalAdicionaisReais)}</span>
                <span>adiantamentos {formatarBRL(paraReais(totalAdiantamentos))}</span>
              </div>
              <p className="font-bold text-foreground">
                Sai em {quinzena.dataPagamento ? formatarDataCurta(quinzena.dataPagamento) : "—"}
                {" · "}{formatarBRL(totalASair)}
              </p>
            </div>

            {/* Admin actions */}
            {isAdmin && (
              <div className="flex gap-2">
                {quinzena.status === "ABERTA" && (
                  <Button
                    variant="outline"
                    className="border-info/30 text-info"
                    onClick={() => setFecharOpen(true)}
                  >
                    Fechar Quinzena
                  </Button>
                )}
                {quinzena.status === "FECHADA" && (
                  <Button onClick={() => setPagarOpen(true)}>
                    Marcar como Paga
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {/* Desktop dialogs */}
        {quinzena && (
          <FecharQuinzenaDialog open={fecharOpen} onClose={() => setFecharOpen(false)} quinzena={quinzena} />
        )}
        {quinzena && (
          <PagarQuinzenaDialog open={pagarOpen} onClose={() => setPagarOpen(false)} quinzena={quinzena} totalASair={totalASair} />
        )}
      </div>

      {/* ── Fase 2: adiantamento e fechamento ── */}
      <GerarAdiantamentosDialog
        open={adiantamentosOpen}
        onOpenChange={setAdiantamentosOpen}
        quinzenaId={quinzena?.id}
      />
      <FechamentoMensalDialog
        open={fechamentoOpen}
        onOpenChange={setFechamentoOpen}
        competenciaInicial={competenciaAtual}
      />

      {/* ── Shared sheets (used by both mobile and desktop) ── */}

      {/* TarefaDiariaSheet — create or edit */}
      {quinzena && (
        <TarefaDiariaSheet
          open={diariaSheetOpen}
          mode={diariaSheetMode}
          quinzenaId={quinzena.id}
          tarefasDoDia={diariaSheetMode === "edit" && editingTarefasDia.length > 0 ? editingTarefasDia : undefined}
          diaria={diariaSheetMode === "edit" && editingTarefasDia.length === 0 ? editingDiaria : undefined}
          dataInicial={format(new Date(), "yyyy-MM-dd")}
          funcionarios={funcionarios}
          atividades={atividades}
          setores={setores}
          onClose={closeDiariaSheet}
          onSuccess={closeDiariaSheet}
        />
      )}

      {/* FecharQuinzenaSheet — mobile */}
      {quinzena && (
        <FecharQuinzenaSheet
          open={fechandoMobile}
          quinzena={quinzena}
          onClose={() => setFechandoMobile(false)}
          onSuccess={() => setFechandoMobile(false)}
        />
      )}

      {/* PagarQuinzenaDialog — reuse for mobile too */}
      {quinzena && (
        <PagarQuinzenaDialog
          open={pagarMobileOpen}
          onClose={() => setPagarMobileOpen(false)}
          quinzena={quinzena}
          totalASair={totalASair}
        />
      )}

      {/* Recibo da quinzena — mesmo diálogo nos dois ramos (mobile e desktop). */}
      {funcDoRecibo && quinzena && (
        <ReciboDialog
          open={!!reciboFuncId}
          onClose={() => setReciboFuncId(null)}
          titulo={tituloRecibo({
            pessoa: { nome: funcDoRecibo.nome, apelido: funcDoRecibo.apelido },
            periodo: `${format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM")} a ${format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM")}`,
            pago: quinzena.status === "PAGA",
          })}
          aviso={quinzena.status === "PAGA"
            ? undefined
            : "A quinzena ainda não foi paga — o texto sai como demonstrativo."}
          texto={textoRecibo}
        />
      )}

      {/* NovaQuinzenaSheet */}
      <NovaQuinzenaSheet
        open={novaQOpen}
        quinzenaAnterior={quinzenas[0] ?? null}
        onClose={() => setNovaQOpen(false)}
        onSuccess={() => setNovaQOpen(false)}
      />

      {/* ── Day detail panel ── */}
      {dayPanel && (() => {
        const dayDiarias = diarias.filter(d => d.funcionarioId === dayPanel.funcId && d.data === dayPanel.dateStr);
        const subtotal = dayDiarias.reduce((acc, d) => acc + parseFloat(d.valor), 0);
        const dateLabel = format(new Date(dayPanel.dateStr + "T00:00:00"), "eee dd/MM", { locale: ptBR });
        const atividadeMap = new Map(atividades.map(a => [a.id, a.nome]));
        const tarefaMap = new Map(tarefasPeriodo.map(t => [t.id, t]));
        const STATUS_CHIP_DIA: Record<string, string> = {
          PENDENTE:     "bg-muted text-muted-foreground",
          EM_ANDAMENTO: "bg-atencao-suave text-atencao",
          CONCLUIDA:    "bg-sucesso-suave text-sucesso",
          FALTOU:       "bg-erro-suave text-erro",
          CANCELADA:    "bg-muted text-muted-foreground",
        };
        const STATUS_LABEL_DIA: Record<string, string> = {
          PENDENTE: "Pendente", EM_ANDAMENTO: "Em andamento",
          CONCLUIDA: "Concluída", FALTOU: "Faltou", CANCELADA: "Cancelada",
        };
        return (
          <Dialog open={!!dayPanel} onOpenChange={open => { if (!open) closeDayPanel(); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-base">
                  {dayPanel.funcNome} — {dateLabel}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                {dayDiarias.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum lançamento neste dia.</p>
                ) : (
                  dayDiarias.map(d => {
                    const linkedTarefa = (d as any).tarefaId ? tarefaMap.get((d as any).tarefaId) : null;
                    const isConcluida = linkedTarefa?.status === "CONCLUIDA";
                    return (
                    <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">
                          {atividadeMap.get(((d as any).atividadeId ?? linkedTarefa?.atividadeId) ?? "") ?? TIPO_LABELS[d.tipo] ?? d.tipo}
                        </span>
                        {(d as any).comentario && (
                          <p className="text-xs text-muted-foreground truncate">{(d as any).comentario}</p>
                        )}
                        {linkedTarefa && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_CHIP_DIA[linkedTarefa.status] ?? "bg-muted text-muted-foreground"}`}>
                              {STATUS_LABEL_DIA[linkedTarefa.status] ?? linkedTarefa.status}
                            </span>
                            {quinzena?.status === "ABERTA" && !isConcluida && (
                              <button
                                onClick={async () => {
                                  await updateTarefa.mutateAsync({ id: linkedTarefa.id, data: { status: "CONCLUIDA" } });
                                  qc.invalidateQueries({ queryKey: ["/api/equipe/diarias"] });
                                }}
                                disabled={updateTarefa.isPending}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-sucesso-suave text-sucesso font-medium hover:bg-sucesso-suave transition-colors disabled:opacity-50"
                              >
                                ✓ Concluir
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="font-semibold text-sucesso shrink-0">{formatMoney(parseFloat(d.valor))}</span>
                      {quinzena?.status === "ABERTA" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs shrink-0"
                          onClick={() => {
                            closeDayPanel();
                            // Fix B: rotear conforme existam tarefas no dia
                            // Diária sem tarefa (órfã/DESCONTO/ADIANTAMENTO) → editor de diária (delete funciona)
                            const temTarefaNoDia = tarefasPeriodo.some(
                              t => t.funcionarioId === dayPanel.funcId && t.data === dayPanel.dateStr
                            );
                            if (temTarefaNoDia) {
                              openEditDia(dayPanel.funcId, dayPanel.funcNome, dayPanel.dateStr);
                            } else {
                              openEditDiaria(d, dayPanel.funcNome);
                            }
                          }}
                        >
                          Editar
                        </Button>
                      )}
                    </div>
                    );
                  })
                )}
                {dayDiarias.length > 1 && (
                  <div className="flex justify-between border-t pt-2 text-sm font-semibold">
                    <span>Subtotal</span>
                    <span className="text-sucesso">{formatMoney(subtotal)}</span>
                  </div>
                )}
                {quinzena?.status === "ABERTA" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => {
                      closeDayPanel();
                      openCreateDiaria();
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Adicionar lançamento
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
      </>
      )}
    </>
  );
}
